import { type ReactElement, useEffect, useState } from 'react';
import { type AnalyticsExportFormat, type AnalyticsExportSource, type AnalyticsExportTemplateFrom } from '@vello/api-client';
import { Button, CheckRow, Field, Notice, SegmentedControl, Select, TextInput, FormStack, Ink, Stack, Text, Row, Spacer, InlineRow } from '@vello/web-theme';
import { type ExportColumnChoice, columnsToSend, exportErrorView, initialExportChoice, moveExportColumn, toggleExportColumn } from '../../../src/index';
import { useAdmin } from '../../session';
import { useConsole } from '../../console';
import { useT } from '../../i18n/provider';
import { useVocab } from '../../i18n/vocab';
import { useRouter } from '../../router';
import { FormDialog } from '../form-dialog';
import { useBiText } from './bi-ui';
import { useExportColumns } from './bi-exports-store';
import { ScheduleDialog } from './ScheduleDialog';

/** What a dialog exports: a source (a query, a saved report, a catalogue report, an Analytics list) or a template; `title` names it for the operator. */
export interface ExportTarget {
  readonly source?: AnalyticsExportSource;
  readonly templateId?: string;
  readonly title?: string;
}

export interface ExportDialogProps {
  readonly open: boolean;
  readonly target: ExportTarget | undefined;
  readonly onClose: () => void;
}

/**
 * "Exportar (segundo plano)" (D532): the choices of an asynchronous export - which columns, in which order, CSV or XLSX, the separator - and the
 * queue. The columns are what the SERVER's plan of the source produces (it is asked when the dialog opens), so only columns that exist are offered.
 * Beside it: keep the choices as a template (optionally shared with the team) and schedule the same export. A refusal stays in the dialog, in plain
 * words; a queued export is followed on the Exportações screen.
 */
export function ExportDialog({ open, target, onClose }: ExportDialogProps): ReactElement | null {
  const { t } = useT();
  const { v } = useVocab();
  const text = useBiText();
  const { runtime } = useAdmin();
  const { toast } = useConsole();
  const { navigate } = useRouter();
  const probe = open && target !== undefined ? { ...(target.source !== undefined ? { source: target.source } : {}), ...(target.templateId !== undefined ? { templateId: target.templateId } : {}) } : undefined;
  const columns = useExportColumns(probe);
  // What the operator changed; until they change something the choices are the server's (a template carries its own).
  const [pickedColumns, setPickedColumns] = useState<ExportColumnChoice | undefined>(undefined);
  const [pickedFormat, setPickedFormat] = useState<AnalyticsExportFormat | undefined>(undefined);
  const [pickedDelimiter, setPickedDelimiter] = useState<',' | ';' | undefined>(undefined);
  const [keep, setKeep] = useState(false);
  const [tplName, setTplName] = useState('');
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [scheduling, setScheduling] = useState(false);
  const probeKey = JSON.stringify(probe ?? null);

  // A new target (or the dialog opening again) starts from the server's choices.
  useEffect(() => {
    setPickedColumns(undefined);
    setPickedFormat(undefined);
    setPickedDelimiter(undefined);
    setProblem(undefined);
    setKeep(false);
    setShared(false);
    setTplName('');
    setScheduling(false);
  }, [open, probeKey]);

  const ready = columns.status === 'ready' ? columns.value : undefined;
  const choice: ExportColumnChoice = pickedColumns ?? (ready !== undefined ? initialExportChoice(ready.columns, ready.defaults.columns) : []);
  const format: AnalyticsExportFormat = pickedFormat ?? ready?.defaults.format ?? 'CSV';
  const delimiter: ',' | ';' = pickedDelimiter ?? ready?.defaults.delimiter ?? ',';
  const setChoice = setPickedColumns;
  const setFormat = setPickedFormat;
  const setDelimiter = setPickedDelimiter;

  if (target === undefined) return null;
  const all = columns.status === 'ready' ? columns.value.columns : [];
  const byId = new Map(all.map((c) => [c.id, c]));
  const unchosen = all.filter((c) => !choice.includes(c.id));
  const personal = columns.status === 'ready' && columns.value.personal;
  // A template keeps a QUERY or a saved report; a catalogue report or an Analytics list is not a query (it is scheduled or exported again from its screen).
  const canTemplate = target.source !== undefined && target.source.kind !== 'CATALOG_REPORT' && target.source.kind !== 'LIST_REPORT';
  const sendColumns = columnsToSend(choice, all);

  const templateFrom = (): AnalyticsExportTemplateFrom | undefined => {
    const s = target.source;
    if (s === undefined) return undefined;
    if (s.kind === 'QUERY') return { kind: 'QUERY', query: s.query };
    if (s.kind === 'SAVED_REPORT') return { kind: 'SAVED_REPORT', reportId: s.reportId };
    return undefined;
  };

  const submit = async (): Promise<void> => {
    if (choice.length === 0) {
      setProblem(t('bi.exp.cols.none'));
      return;
    }
    setBusy(true);
    setProblem(undefined);
    const body = { ...(target.source !== undefined ? { source: target.source } : {}), ...(target.templateId !== undefined ? { templateId: target.templateId } : {}), columns: sendColumns, format, ...(format === 'CSV' ? { delimiter } : {}) };
    const res = await runtime.analyticsBi.enqueueExport(body);
    if (!res.ok) {
      setBusy(false);
      setProblem(t(exportErrorView(res.error).messageId));
      return;
    }
    let note: string | undefined;
    const from = templateFrom();
    if (keep && from !== undefined) {
      const saved = await runtime.analyticsBi.saveExportTemplate({ name: tplName, from, columns: sendColumns, format, ...(format === 'CSV' ? { delimiter } : {}), shared });
      if (!saved.ok) note = t('bi.exp.tpl.failed', { reason: t(exportErrorView(saved.error).messageId) });
      else toast({ tone: 'success', message: t('bi.exp.tpl.saved', { name: saved.value.template.name }) });
    }
    setBusy(false);
    toast({ tone: note === undefined ? 'success' : 'warning', message: note ?? t('bi.exp.queued') });
    onClose();
    navigate('bi_exports');
  };

  return (
    <>
      <FormDialog open={open && !scheduling} id="bi-export" icon="download" title={target.title ?? t('bi.exp.dialog.title')} body={t('bi.exp.dialog.body')} busy={busy} initialFocusId="bi-export-first" onCancel={onClose}>
        <FormStack gap={4}
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          noValidate
        >
          {columns.status === 'loading' && <Ink as="p" tone="secondary">{t('ui.loading')}</Ink>}
          {columns.status === 'error' && <Notice tone="warning">{t(columns.error.messageId)}</Notice>}
          {personal && <Notice tone="warning">{t('bi.exp.dialog.personal')}</Notice>}
          {columns.status === 'ready' && (
            <>
              <Stack as="fieldset" gap={1}>
                <Ink as="legend" strong>{t('bi.exp.cols')}</Ink>
                <Text role="caption" tone="secondary">{t('bi.exp.cols.hint')}</Text>
                {choice.map((id, i) => {
                  const col = byId.get(id);
                  const name = text.columnName(id);
                  return (
                    <Row gap={2} align="center" wrap key={id}>
                      <CheckRow id={`bi-export-col-${id}`} checked onChange={(on) => setChoice(toggleExportColumn(choice, id, on))}>
                        <span id={i === 0 ? 'bi-export-first' : undefined}>{name}</span>
                        {col?.personal === true && <Text role="caption" tone="warning">{t('bi.exp.col.person')}</Text>}
                        {col?.money === true && <Text role="caption" tone="secondary">{t('bi.exp.col.money')}</Text>}
                      </CheckRow>
                      <Spacer />
                      <InlineRow gap={1} align="center">
                        <Button size="sm" variant="ghost" icon="chevronUp" aria-label={t('bi.exp.col.up', { name })} disabled={i === 0} onClick={() => setChoice(moveExportColumn(choice, id, -1))} className="vello-admin-a-tap" />
                        <Button size="sm" variant="ghost" icon="chevronDown" aria-label={t('bi.exp.col.down', { name })} disabled={i === choice.length - 1} onClick={() => setChoice(moveExportColumn(choice, id, 1))} className="vello-admin-a-tap" />
                      </InlineRow>
                    </Row>
                  );
                })}
                {unchosen.map((col) => (
                  <CheckRow key={col.id} id={`bi-export-col-${col.id}`} checked={false} onChange={(on) => setChoice(toggleExportColumn(choice, col.id, on))}>
                    <Ink tone="secondary">{text.columnName(col.id)}</Ink>
                    {col.personal && <Text role="caption" tone="warning">{t('bi.exp.col.person')}</Text>}
                  </CheckRow>
                ))}
              </Stack>
              <Field label={t('bi.exp.format')} htmlFor="bi-export-format">
                <SegmentedControl<AnalyticsExportFormat>
                  ariaLabel={t('bi.exp.format')}
                  value={format}
                  onChange={setFormat}
                  options={[
                    { id: 'CSV', label: v('analytics.export_format', 'CSV') },
                    { id: 'XLSX', label: v('analytics.export_format', 'XLSX') },
                  ]}
                />
              </Field>
              {format === 'CSV' && (
                <Field label={t('bi.exp.delimiter')} htmlFor="bi-export-delimiter">
                  <Select id="bi-export-delimiter" value={delimiter} onChange={(e) => setDelimiter(e.target.value === ';' ? ';' : ',')}>
                    <option value=",">{t('bi.exp.delimiter.comma')}</option>
                    <option value=";">{t('bi.exp.delimiter.semicolon')}</option>
                  </Select>
                </Field>
              )}
              {canTemplate && (
                <Stack gap={2}>
                  <CheckRow id="bi-export-keep" checked={keep} onChange={setKeep}>
                    {t('bi.exp.tpl.keep')}
                  </CheckRow>
                  {keep && (
                    <>
                      <Field label={t('bi.exp.tpl.name')} htmlFor="bi-export-tpl-name">
                        <TextInput id="bi-export-tpl-name" value={tplName} maxLength={80} onChange={(e) => setTplName(e.target.value)} />
                      </Field>
                      <CheckRow id="bi-export-share" checked={shared} onChange={setShared}>
                        {t('bi.exp.tpl.share')}
                      </CheckRow>
                    </>
                  )}
                </Stack>
              )}
            </>
          )}
          {problem !== undefined && <Notice tone="warning">{problem}</Notice>}
          <Row gap={2} align="stretch" wrap>
            <Button className="vello-admin-a-btn-grow" variant="ghost" onClick={onClose}>
              {t('ui.cancel')}
            </Button>
            {columns.status === 'ready' && (
              <Button className="vello-admin-a-btn-grow" variant="secondary" disabled={busy || choice.length === 0} onClick={() => setScheduling(true)}>
                {t('bi.exp.schedule')}
              </Button>
            )}
            <Button className="vello-admin-a-btn-grow" type="submit" variant="primary" loading={busy} disabled={columns.status !== 'ready' || choice.length === 0 || (keep && tplName.trim() === '')}>
              {t('bi.exp.queue')}
            </Button>
          </Row>
        </FormStack>
      </FormDialog>
      {scheduling && (
        <ScheduleDialog
          open
          preset={{ ...(target.source !== undefined ? { source: target.source } : {}), ...(target.templateId !== undefined ? { templateId: target.templateId } : {}), columns: sendColumns, format, delimiter }}
          onClose={() => setScheduling(false)}
          onSaved={() => {
            setScheduling(false);
            onClose();
            navigate('bi_exports');
          }}
        />
      )}
    </>
  );
}

